import { Box, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";

import Session from "../../../../appfw/authentication/Session.ts";
import { SessionContext } from "../../../../appfw/authentication/SessionProvider.tsx";
import { useW2d2dPod } from "../../hooks/useW2d2dPod.ts";
import { decodeResourceUri } from "../../../../eventer/utils/uri-encoding.ts";
import { ResourceWrapper } from "../../../../eventer-pod/client/ResourceWrapper.ts";
import { useLoadingControl } from "../../../../appfw/loading/use-loading-control.tsx";
import { w2d2dApiClient, Idea } from "../../../w2d2d-api-client.ts";
import { useIdeasControl } from "../../hooks/useIdeasControl.tsx";
import LoadingScreen from "../../../../appfw/ui/loading/loading-screen.tsx";
import EventIdeaView from "../../components/events/event-idea-view.tsx";

/**
 * Event View
 * 
 * Shows details of an event loaded by its encoded URI.
 * Can be used as a route component (using useParams) or as a standalone component (using props).
 */
export interface EventViewProps {
    encodedUri?: string;
}

export default function EventView(props: EventViewProps) {
    const params = useParams<{ encodedUri: string }>();
    const encodedUri = props.encodedUri || params.encodedUri;

    const session = useContext<undefined | null | Session>(SessionContext);
    const pod = useW2d2dPod()

    // Decode the URI from the path
    let eventUri: string;
    try {
        if (!encodedUri) throw new Error("No URI provided");
        eventUri = decodeResourceUri(encodedUri)
    } catch (err) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">Ungültige Veranstaltung</Typography>
            </Box>
        );
    }

    const loadingControl = useLoadingControl('event-view');
    const [event, setEvent] = useState<ResourceWrapper | undefined>(undefined);
    const [idea, setIdea] = useState<Idea | null | undefined>(undefined);
    const { getByEventUri } = useIdeasControl();

    useEffect(() => {
        if (session === undefined || !pod) return;

        const sessionId = session?.id;

        loadingControl.withLoading(async () => {
            try {
                const resource = await pod.getResource({
                    uri: eventUri,
                    sessionId,
                    importOptions: { ifMissing: true },
                });
                setEvent(resource || undefined);
            } catch (err) {
                console.error('Failed to load event:', err);
                setEvent(undefined);
            }
        });
    }, [pod, session, eventUri]);

    useEffect(() => {
        if (session === undefined) return;

        loadingControl.withLoading(async () => {
            try {
                const loadedIdea = await w2d2dApiClient.getIdeaByEvent({ uri: eventUri });
                setIdea(loadedIdea);
            } catch (err) {
                console.error('Failed to load idea by event:', err);
                setIdea(null);
            }
        });
    }, [session, eventUri]);

    // Update idea from IdeasControl when it changes (e.g., via web push)
    useEffect(() => {
        const updatedIdea = getByEventUri(eventUri);
        if (updatedIdea) {
            setIdea(updatedIdea);
        }
    }, [getByEventUri, eventUri]);

    if (!pod || loadingControl.isLoading || session === undefined || idea === undefined) {
        return <LoadingScreen message="Lade Veranstaltung" />
    }

    if (!event) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">Veranstaltung nicht gefunden</Typography>
            </Box>
        );
    }

    return (
        <EventIdeaView event={event} idea={idea} />
    );
}
